import { Fragment, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from "~/redux/api/notifications";
import type { INotification } from "~/routes/portal/type";

import {
  Icon,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Skeleton,
} from "../ui";
import { NotificationRow } from "./notification-item";
import {
  extractNotifications,
  groupNotifications,
  routeFor,
} from "./notification-item.utils";

const NOTIFICATIONS_ROUTE = "/notifications";

const Notifications = () => {
  const navigate = useNavigate();

  const { data: resp, isLoading, isFetching, isError } =
    useGetNotificationsQuery({
      page: 1,
      pageSize: 8,
    });
  const { data: unreadResp } = useGetUnreadCountQuery();
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();

  const notifications = useMemo(() => extractNotifications(resp), [resp]);

  const unreadCount: number = useMemo(() => {
    const r = unreadResp as any;
    const fromApi = r?.data?.count ?? r?.result?.count ?? r?.count;
    if (typeof fromApi === "number") return fromApi;
    return notifications.filter((n) => !n.isRead).length;
  }, [unreadResp, notifications]);

  const grouped = useMemo(
    () => groupNotifications(notifications),
    [notifications],
  );

  const handleClick = async (item: INotification) => {
    if (!item.isRead) {
      try {
        await markRead({ id: item.id }).unwrap();
      } catch (err) {
        console.error("markNotificationRead failed", { id: item.id }, err);
      }
    }
    navigate(routeFor(item));
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead().unwrap();
    } catch (err) {
      console.error("mark all read failed", err);
      toast.error("Couldn't mark all as read. Please try again.");
    }
  };

  return (
    <Popover>
      <PopoverTrigger className="relative" aria-label="Notifications">
        <div className="relative">
          {unreadCount > 0 && (
            <div className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 font-Medium text-[9px] text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </div>
          )}
          <Icon name="Bell" className="size-6" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        onWheel={(event) => event.stopPropagation()}
        align="end"
        className="w-[26rem] p-0 shadow-large"
      >
        <div className="flex items-center justify-between gap-4 px-5 pt-5">
          <div className="flex items-center gap-2">
            <span className="font-Medium text-xl">Notifications</span>
            {unreadCount > 0 && (
              <span
                className="rounded-full bg-info-100 px-2 py-0.5 text-xs font-Medium text-info-900"
                aria-live="polite"
              >
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <PopoverClose
              aria-label="Open notification centre"
              title="Expand"
              onClick={() => navigate(NOTIFICATIONS_ROUTE)}
            >
              <div className="flex size-9 items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200">
                <Icon name="Maximize2" className="size-4" />
              </div>
            </PopoverClose>
            <PopoverClose aria-label="Close">
              <div className="flex size-9 items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200">
                <Icon name="X" className="size-4" />
              </div>
            </PopoverClose>
          </div>
        </div>

        <div className="max-h-[380px] w-full overflow-y-auto px-3 py-3" role="list">
          {isLoading &&
            [0, 1, 2].map((i) => (
              <div key={i} className="flex gap-4 px-2 py-3">
                <Skeleton className="h-10 w-10 rounded-full bg-neutral-100" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-40 bg-neutral-100" />
                  <Skeleton className="h-8 w-full rounded-md bg-neutral-100" />
                </div>
              </div>
            ))}

          {!isLoading && isError && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-danger-100 text-danger-500">
                <Icon name="TriangleAlert" className="size-7" />
              </div>
              <h3 className="mb-1 text-base font-bold text-neutral-800">
                Couldn&apos;t load notifications
              </h3>
              <p className="text-sm text-neutral-500">
                Something went wrong. Please try again in a moment.
              </p>
            </div>
          )}

          {!isLoading && !isError && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
                <Icon name="BellOff" className="size-7" />
              </div>
              <h3 className="mb-1 text-base font-bold text-neutral-800">
                No notifications yet
              </h3>
              <p className="text-sm text-neutral-500">
                You&apos;ll get notified about assignments, approvals and
                reminders.
              </p>
            </div>
          )}

          {!isLoading &&
            grouped.map(({ label, item }, idx) => (
              <Fragment key={item.id ?? idx}>
                {label && (
                  <div className="px-2 pb-1 pt-3 text-xs font-Medium uppercase tracking-wide text-neutral-400">
                    {label}
                  </div>
                )}
                <NotificationRow item={item} onClick={handleClick} />
              </Fragment>
            ))}
        </div>

        <Separator className="bg-neutral-200" />
        <div className="flex items-center justify-between px-4 py-2.5">
          <PopoverClose
            className="flex items-center gap-1.5 text-sm text-primary-500 hover:underline"
            onClick={() => navigate(NOTIFICATIONS_ROUTE)}
          >
            <Icon name="Maximize2" className="size-4" />
            View all notifications
          </PopoverClose>
          <button
            type="button"
            disabled={unreadCount === 0 || isFetching}
            className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 disabled:cursor-not-allowed disabled:text-neutral-300"
            onClick={handleMarkAllRead}
          >
            <Icon name="CheckCheck" className="size-4" />
            Mark all as read
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default Notifications;
